import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// -------------------------------------------------------------
// usePlaceName
// Fetches a human-readable place name from
// coordinates using the Nominatim reverse
// geocoding API.
//
// TanStack Query handles:
// - caching (same coords won't refetch)
// - loading and error states automatically
//
// To avoid spamming Nominatim when GPS coordinates drift slightly,
// we normalize the incoming coordinates before using them as a
// query key. Small movements then reuse the same cached place name.
// -------------------------------------------------------------

// Round coordinates to 3 decimal places.
// This groups nearby points into roughly 100m buckets, which is
// usually stable enough for a place-name label.
const normalizeCoords = (coords) => {
  if (!coords || coords.length < 2) return null;

  const [lat, lon] = coords;
  return [Number(lat.toFixed(3)), Number(lon.toFixed(3))];
};

// Fetches the place name for given coordinates using Nominatim API.
const fetchPlaceName = async ([lat, lon]) => {
  let res;

  try {
    res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=en`,
    );
  } catch (err) {
    if (err instanceof Error && err.message === "Failed to fetch") {
      throw new Error(
        "The location service is temporarily busy or rate-limited. Please wait a few seconds and try again. If the issue continues, wait up to 1 minute before retrying.",
      );
    }

    throw err;
  }

  if (!res.ok) throw new Error("Failed to fetch place name");

  const data = await res.json();
  const { village, town, city, county, state, country } = data.address;

  // Pick the most specific available place name.
  const localName = village || town || city || county || "Unknown area";
  return `${localName}, ${state}, ${country}`;
};

// Custom hook to get place name from coordinates.
const usePlaceName = (coords) => {
  const { t } = useTranslation();

  const normalizedCoords = normalizeCoords(coords);

  const {
    data: placeName,
    isPending,
    isError,
  } = useQuery({
    // TanStack Query caches per normalized coordinate pair.
    queryKey: ["placeName", normalizedCoords],

    // Reverse geocode using the normalized coordinates.
    queryFn: () => fetchPlaceName(normalizedCoords),

    // Only run when coordinates are available.
    enabled: !!normalizedCoords,

    // Place names rarely change, so keep them fresh for 10 minutes.
    staleTime: 1000 * 60 * 10,

    // Reuse the last successful place name while a new bucket loads.
    placeholderData: (previousData) => previousData ?? t("common.locating"),
  });

  return {
    placeName: isError
      ? t("toastMessages.locationServiceBusy")
      : (placeName ?? t("common.locating")),
    isLoading: isPending,
  };
};

export default usePlaceName;
